/*=========================================================================================
  File Name: actions.js
  Description: Vuex Store - actions
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
==========================================================================================*/

import AppMainAxios from '../http/requests/app/index.js';
import ShopDrawingsAxios from '../http/requests/shop-drawings/index.js';
import jwt from '../http/requests/auth/jwt/index.js';
import admin from '../http/requests/admin/index';

const actions = {

    // /////////////////////////////////////////////
    // COMPONENTS
    // /////////////////////////////////////////////

    // Vertical NavMenu
    updateVerticalNavMenuWidth ({ commit }, width) {
        commit('UPDATE_VERTICAL_NAV_MENU_WIDTH', width);
    },

    /**
     * Opens Shop Drawings priority edit modal.
     *
     * @param commit Vuex commit function
     */
    openShopDrawingsPriorityEditModal: function ({ commit }) {
        commit('TOGGLE_OPEN_PRIORITY_EDIT_MODAL', true);
    },

    /**
     * Closes Shop Drawings priority edit modal.
     *
     * @param commit Vuex commit function
     */
    closeShopDrawingsPriorityEditModal: function ({ commit }) {
        commit('TOGGLE_CLOSE_PRIORITY_EDIT_MODAL', false);
    },
    /**
     * Open Timeline for Action Required Priority modal
     *
     * @param commit Vuex commit function
     */
    openActionRequiredPriorityTimelineModal: function ({ commit }) {
        commit('TOGGLE_OPEN_ACTION_REQUIRED_TIMELINE_MODAL', true);
    },
    /**
     * Close Timeline for Action Required Priority modal
     *
     * @param commit Vuex commit function
     */
    closeActionRequiredPriorityTimelineModal: function ({ commit }) {
        commit('TOGGLE_CLOSE_ACTION_REQUIRED_TIMELINE_MODAL', false);
    },

    getPriorityItems: function ({ commit }) {
        return new Promise((resolve, reject) => {
            ShopDrawingsAxios.getPriorityItems().then(response => {
                if (!response.data.success) {
                    reject({ message: response.data.message });
                }

                commit('SET_PRIORITY_EDIT_MODAL_DATA', response.data.data);

                resolve(response);
            }).catch(error => {
                reject(error);
            });
        });
    },

    getPriorityOptions: function ({ commit }) {
        return new Promise((resolve, reject) => {
            ShopDrawingsAxios.getPriorityOptions().then(response => {
                if (!response.data.success) {
                    reject({ message: response.data.message });
                }

                commit('SET_PRIORITY_OPTIONS', response.data.data);

                resolve(response);
            }).catch(error => {
                reject(error);
            });
        });
    },

    setPriorityItems: function ({ commit }, payload) {
        return new Promise((resolve, reject) => {
            ShopDrawingsAxios.setPriorityItems(payload).then(response => {
                if (!response.data.success) {
                    reject({ message: response.data.message });
                }

                resolve(response);
            }).catch(error => {
                reject(error);
            });
        });
    },

    // VxAutoSuggest
    updateStarredPage ({ commit }, payload) {
        commit('UPDATE_STARRED_PAGE', payload);
    },

    // The Navbar
    arrangeStarredPagesLimited ({ commit }, list) {
        commit('ARRANGE_STARRED_PAGES_LIMITED', list);
    },
    arrangeStarredPagesMore ({ commit }, list) {
        commit('ARRANGE_STARRED_PAGES_MORE', list);
    },

    // /////////////////////////////////////////////
    // UI
    // /////////////////////////////////////////////

    toggleContentOverlay ({ commit }) {
        commit('TOGGLE_CONTENT_OVERLAY');
    },

    // /////////////////////////////////////////////
    // User/Account
    // /////////////////////////////////////////////

    updateUserInfo ({ commit }, payload) {
        commit('UPDATE_USER_INFO', payload);
    },

    async initApp ({
        dispatch,
        commit,
    }, payload) {
        commit('auth/SET_BEARER', localStorage.getItem('accessToken'));

        window.addEventListener("message", (event) => {
            console.log('message received', event);
            if (typeof event.data != 'object' || event.data == null) {
                return;
            }

            if (event.data.hasOwnProperty('type') && event.data.type == 'zoho_desk_login') {
                dispatch('auth/loginWithZohoDesk', event.data.user)
            }
        });

        jwt.init();

        await dispatch('getAppInfo');

        if (window.location.href.indexOf('/login/callback') === -1 && window.location.href.indexOf('/login/swagger') === -1) {
            await dispatch('getUserInfo');
        }
    },

    getAppInfo ({ commit }) {
        return new Promise((resolve, reject) => {
            AppMainAxios.info().then(response => {
                if (!response.data.success) {
                    reject({ message: response.data.message });
                }

                commit('UPDATE_APP_INFO', response.data.data);

                resolve(response);
            }).catch(error => {
                reject(error);
            });
        });
    },

    getUserInfo ({ commit }) {
        return new Promise((resolve, reject) => {
            AppMainAxios.userInfo().then(response => {
                if (!response.data.success) {
                    reject({ message: response.data.message });
                }

                commit('UPDATE_USER_INFO', response.data.data.user);
                commit('UPDATE_IMPERSONATOR_INFO', response.data.data.impersonator);
                commit('UPDATE_USER_ACCESS', response.data.data.permissions);

                resolve(response);
            }).catch(error => {
                reject(error);
            });
        });
    },

    /**
     * Get Media load url
     *
     * @param commit Vuex commit function
     * @param payload Payload data
     *
     * @returns {Promise<unknown>}
     */
    getMediaLoadUrlResponse: function ({ commit }, payload) {
        return new Promise((resolve, reject) => {
            AppMainAxios.getMediaLoadUrlResponse(payload.url).then(response => {
                if (!response.data.success) {
                    reject({ message: response.data.message });
                }
                resolve(response);
            }).catch(error => {
                reject(error);
            });
        });
    },

    /**
     * Retrieves damage image data URI to break CORS errors
     *
     * @param _
     * @param id media ID
     *
     * @returns {Promise<unknown>}
     */
    getDataURI (_, id) {
        return new Promise((resolve, reject) => {
            AppMainAxios.getDataURI(id)
                .then(response => {
                    if (!response.data.success) {
                        reject({ message: response.data.message });
                    }
                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    /**
     * Stores uploaded file to media processing list.
     *
     * @param data Data object
     *
     * @returns {Promise<unknown>}
     */
    storeMediaProcessing ({}, data) {
        return new Promise((resolve, reject) => {
            AppMainAxios.storeMediaProcessing(data)
                .then(response => {
                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    /**
     * Stores uploaded file to media processing list.
     *
     * @param data Data object
     *
     * @returns {Promise<unknown>}
     */
    storeDoMediaProcessing ({}, data) {
        return new Promise((resolve, reject) => {
            AppMainAxios.storeDoMediaProcessing(data)
                .then(response => {
                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    /**
     * Triggers Artisan command execution.
     *
     * @param command Artisan command
     *
     * @returns {Promise<unknown>}
     */
    executeCommand ({}, command) {
        return new Promise((resolve, reject) => {
            admin.executeCommand(command)
                .then(response => {
                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                });
        });
    },
};

export default actions;
